import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { LoadingButton } from "../components/Spinner";
import PasswordInput from "../components/PasswordInput";
import { AuthShell, AuthLogo, FormError, GoogleBlock, RotatingWords, authQuery, parseErr } from "../components/AuthBits";

export default function SignUp() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const next = sp.get("next") ?? "/";
  const invite = sp.get("invite") ?? "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const register = useMutation({
    mutationFn: () => api.register({ email: email.trim().toLowerCase(), password, name: name.trim() || undefined, invite: invite || undefined }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      if (invite) await qc.invalidateQueries({ queryKey: ["workspaces"] });
      nav(next);
    },
    onError: (e) => setErr(parseErr(e, "Couldn't create an account.")),
  });

  return (
    <AuthShell>
      <div className="text-center space-y-3">
        <AuthLogo />
        {/* Hard line break: with a single wrapping line, the width
            animation crosses the wrap threshold mid-transition and a
            word snaps to the next line — a discrete jump no easing can
            hide. Fixed two-line structure = only smooth motion. */}
        <h1 className="text-2xl font-bold tracking-tight">
          The <RotatingWords words={["API client", "flow runner", "webhook inbox"]} />
          <br />your team can share.
        </h1>
        <p className="text-sm muted">
          Free forever — unlimited workspaces, teammates, and history.
        </p>
      </div>

      <GoogleBlock sp={sp} />

      <form
        className="space-y-3"
        onSubmit={(e) => { e.preventDefault(); setErr(null); register.mutate(); }}
      >
        <input
          className="input"
          type="text"
          placeholder="Your name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
        />
        <input
          className="input"
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <PasswordInput
          required
          minLength={8}
          placeholder="Choose a password (8+ chars)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
        <FormError msg={err} />
        <LoadingButton
          type="submit"
          className="btn-primary w-full justify-center"
          pending={register.isPending}
          pendingLabel="Creating…"
        >
          Create account
        </LoadingButton>
      </form>

      <div className="flex items-center justify-end text-xs muted">
        <Link to={`/login${authQuery(sp)}`} className="hover:underline">
          Already have an account? <b className="font-semibold text-text">Sign in</b>
        </Link>
      </div>
    </AuthShell>
  );
}
